import { z } from 'zod';
import { AssistantHandoffContextEngineeringPlanType, AssistantToolType, VoiceCallTransferCallMode, VoiceCallTransferCallToolDestinationType, } from './constants.js';
/**
 * One place a transfer tool can send the caller.
 *
 * Stated as one shape rather than the mode-by-mode union the write path validates: which of the
 * optional fields a destination carries follows from `transferMode` and `type`, and a reader
 * needs the destination as configured, not the rules for configuring one.
 */
export const AssistantTransferDestinationSchema = z.object({
    type: z.enum(VoiceCallTransferCallToolDestinationType),
    transferMode: z.enum(VoiceCallTransferCallMode),
    description: z
        .string()
        .describe('What this destination is for, which is how the assistant chooses between them.'),
    number: z.string().optional().describe('The number dialed, for a Number destination.'),
    extension: z.string().optional(),
    callerIdOverride: z.string().optional().describe('The number the destination sees calling.'),
    sipUri: z.string().optional().describe('The address dialed, for a Sip destination.'),
    customerMessage: z.string().optional().describe('Spoken to the caller before the transfer.'),
    destinationMessage: z.string().optional().describe('Spoken to whoever answers the transfer.'),
    summaryMessagesSystemPrompt: z
        .string()
        .optional()
        .describe('The prompt used to summarize the call for whoever answers the transfer.'),
});
/** One assistant a handoff tool can pass the conversation to. */
export const AssistantHandoffDestinationSchema = z.object({
    targetAssistantId: z.uuid(),
    description: z.string(),
    contextEngineeringPlan: z
        .object({
        type: z.enum(AssistantHandoffContextEngineeringPlanType),
        maxMessages: z.number().optional(),
    })
        .optional()
        .describe('How much of the conversation so far the receiving assistant is given.'),
});
/**
 * The configuration particular to a tool's type.
 *
 * Transfer and handoff tools carry their destinations here and a decision tree tool the tree it
 * walks; every other type stores whatever its type defines, which is passed through as written.
 */
export const AssistantToolMetadataSchema = z.union([
    z.object({ destinations: z.array(AssistantTransferDestinationSchema) }),
    z.object({ destinations: z.array(AssistantHandoffDestinationSchema) }),
    z.object({ decisionTreeId: z.uuid() }),
    z.record(z.string(), z.unknown()),
]);
/**
 * One tool an assistant may call mid-conversation, as configured.
 *
 * `parameters` is deliberately absent: the column exists but nothing reads it, so publishing it
 * would describe an argument list the assistant never receives.
 */
export const AssistantToolDefinitionSchema = z.object({
    id: z.uuid(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    type: z.enum(AssistantToolType),
    name: z.string().describe('The name the assistant calls the tool by.'),
    organizationId: z.uuid(),
    description: z
        .string()
        .nullable()
        .describe('What the assistant is told the tool does, which is how it decides to call it.'),
    summary: z.string().nullable(),
    startMessage: z.string().nullable().describe('Spoken when the tool is called.'),
    completeMessage: z.string().nullable().describe('Spoken when the tool succeeds.'),
    failedMessage: z.string().nullable().describe('Spoken when the tool fails.'),
    delayedMessage: z.string().nullable().describe('Spoken when the tool is still running.'),
    delayThresholdMilliseconds: z
        .number()
        .nullable()
        .describe('How long the tool may run before delayedMessage is spoken.'),
    timeoutSeconds: z.number().nullable().describe('How long the tool may run before it fails.'),
    endCallAfterCompleteMessageSpoken: z.boolean().nullable(),
    endCallAfterFailedMessageSpoken: z.boolean().nullable(),
    metadata: AssistantToolMetadataSchema.nullable(),
    deletedTimestamp: z.iso
        .datetime()
        .nullable()
        .describe('Set when the tool was retired; a retired tool is not offered on a call.'),
});
//# sourceMappingURL=tool.js.map